import { useQuery, type QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext, retainSearchParams } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { NoAccessScreen, SignInScreen } from '../components/shell/AccessGate';
import { AppShell } from '../components/shell/AppShell';
import { RouteErrorMessage, RouteNotFound } from '../components/shell/RouteError';
import { validateShellSearch } from '../components/shell/shellState';
import { MfaSetupScreen } from '../features/auth/MfaSetupScreen';
import { MfaVerifyScreen } from '../features/auth/MfaVerifyScreen';
import { meQuery } from '../lib/session';

export interface RouterContext {
  readonly queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: validateShellSearch,
  search: { middlewares: [retainSearchParams(['issuer'])] },
  // Who is signed in, before any screen loads its data (decisions-log W8).
  beforeLoad: async ({ context }) => ({ me: await context.queryClient.ensureQueryData(meQuery) }),
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: RouteNotFound,
});

function RootComponent() {
  const { data: me } = useQuery(meQuery);
  if (me === null) return <SignInScreen />;
  // The second factor comes before anything else, the «no access» screen included (W20).
  if (me?.mfa === 'setup') return <MfaSetupScreen email={me.user?.email ?? ''} />;
  if (me?.mfa === 'verify') return <MfaVerifyScreen email={me.user?.email ?? ''} />;
  if (me && me.mode === 'online' && me.user?.status !== 'active') return <NoAccessScreen me={me} />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/** Last-resort boundary when the shell itself fails: the error still renders, never a blank page. */
function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="p-5">
      <RouteErrorMessage error={error} onRetry={reset} />
    </div>
  );
}
